import { AfterViewInit, Component, inject, OnInit, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { UserSetting } from 'src/app/user/models';
import { CurrentUserService, UserSettingsService } from 'src/app/user/services';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { menuGroups, menuItems } from './menu-definition';
import { MenuPageGroup, MenuPageGroupDefinition } from './models/menu-page-group';
import { MenuPageItem, MenuPageItemDefinition } from './models/menu-page-item';
import { LayoutContainerComponent } from '../../controls/components/basic/layout-container/layout-container.component';
import { ContainerFluidDirective } from '../../controls/directives/container-fluid.directive';
import { LayoutRowComponent } from '../../controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from '../../controls/components/basic/layout-column/layout-column.component';
import { DxAutocompleteComponent, DxAutocompleteModule } from 'devextreme-angular';
import { MenuPageGroupComponent } from './components/menu-page-group/menu-page-group.component';
import { MenuPageItemComponent } from './components/menu-page-item/menu-page-item.component';

@Component({
  selector: 'app-developer-page',
  templateUrl: './developer-page.component.html',
  styleUrls: ['./developer-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LayoutContainerComponent, ContainerFluidDirective, LayoutRowComponent, LayoutColumnComponent, DxAutocompleteModule, MenuPageGroupComponent, MenuPageItemComponent]
})
export class DeveloperPageComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private userSettingsService = inject(UserSettingsService);
  private loadingPanelService = inject(LoadingPanelService);
  private currentUserService = inject(CurrentUserService);
  private searchText = viewChild<DxAutocompleteComponent>('searchText');

  favoriteSetting = new UserSetting('DevFavorites', '', '[]', '');
  groups: MenuPageGroup[] = [];
  items: MenuPageItem[] = [];
  favoriteItems: MenuPageItem[] = [];

  ngOnInit(): void {
    this.items = this.loadMenuPageItems(menuItems);
    this.groups = this.loadMenuPageGroups(menuGroups, this.items);
    void this.loadFavorites();
  }

  ngAfterViewInit(): void {
    this.searchText().instance.focus();
  }

  search(filterExpr: string) {
    const upperFilter = filterExpr?.toUpperCase();
    this.items.forEach(item => item.highlighted = item.name.toUpperCase().includes(upperFilter));
  }

  onValueChanged(e: { value: string }) {
    this.search(e.value)
  }

  onSelectionChanged(e: { selectedItem: MenuPageItem }) {
    if (e.selectedItem) {
      void this.router.navigate([e.selectedItem.link]);
    }
  }

  onFavoriteChanged(e: { id: number, value: boolean }) {
    if (e.value === true) {
      const favItem = this.items.find(i => i.id === e.id)
      this.favoriteItems.push(favItem)
    }
    else {
      this.favoriteItems = this.favoriteItems.filter(i => i.id !== e.id)
    }

    this.favoriteSetting.Value1 = JSON.stringify(this.favoriteItems.map(i => i.key));
    void this.userSettingsService.saveSettings([this.favoriteSetting]);
  }

  isFavorite(id: number) {
    return this.favoriteItems.some(i => i.id === id);
  }

  loadMenuPageGroups(groupDefinitions: MenuPageGroupDefinition[], items: MenuPageItem[]): MenuPageGroup[] {
    // Extract unique group names from the items array
    const groupsWithItems = new Set(items.map(item => item.group));

    return groupDefinitions
      // Filter the group definitions to include only those that have items
      .filter(group => groupsWithItems.has(group.name))
      // Map each group definition to a MenuPageGroup object with an id, name, and column
      .map((group, index) => ({
        id: index + 1, // Assign a unique id based on the index
        name: group.name,
        column: group.column
      } as MenuPageGroup));
  }

  loadMenuPageItems(itemDefinitions: MenuPageItemDefinition[]): MenuPageItem[] {
    return itemDefinitions
      // Filter the item definitions based on user permissions
      .filter(item => !item.permissions || item.permissions.some(permission => this.currentUserService.hasPermission(permission)))
      // Map each item definition to a MenuPageItem object with an id, key, name, group, link, and highlighted flag
      .map((item, index) => ({
        id: index + 1, // Assign a unique id based on the index
        key: item.key,
        name: item.name,
        group: item.group,
        link: item.link,
        highlighted: false // Initialize the highlighted flag to false
      } as MenuPageItem));
  }

  async loadFavorites(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading favorites');

      const setting = await this.userSettingsService.getSingleSetting('DevFavorites');
      this.favoriteSetting = setting || this.favoriteSetting;

      const favItemKeys = JSON.parse(this.favoriteSetting.Value1) as string[];
      this.favoriteItems = this.items.filter(item => favItemKeys.includes(item.key));
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }
}
